/**
 * The console's only way to the data: the public HTTP API (design 16.5). Errors arrive as
 * RFC 9457 problem+json and are thrown as ApiError so screens can show field messages.
 */
import { accessToken, signOut } from "./auth";
import { cfg } from "./config";

export interface ProblemField {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: ProblemField[] = [],
  ) {
    super(message);
  }
}

type Query = Record<string, string | number | boolean | undefined>;

export async function api<T>(method: string, path: string, opts: { body?: unknown; query?: Query; dryRun?: boolean } = {}): Promise<T> {
  const url = new URL(`${cfg().apiUrl.replace(/\/$/, "")}${path}`);
  for (const [k, v] of Object.entries(opts.query ?? {})) if (v !== undefined) url.searchParams.set(k, String(v));
  if (opts.dryRun) url.searchParams.set("dry_run", "true");
  const token = await accessToken();
  const res = await fetch(url, {
    method,
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(opts.body !== undefined ? { "content-type": "application/json" } : {}),
    },
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
  });
  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const p = (data ?? {}) as { code?: string; detail?: string; title?: string; errors?: ProblemField[] };
    if (res.status === 401) signOut();
    throw new ApiError(res.status, p.code ?? "error", p.detail ?? p.title ?? `HTTP ${res.status}`, p.errors ?? []);
  }
  return data as T;
}

export const get = <T>(path: string, query?: Query): Promise<T> => api<T>("GET", path, query ? { query } : {});

/** Run a write as a dry run first (rule 7), then for real; the dry run's errors stop it. */
export async function checkedWrite<T>(method: string, path: string, body?: unknown): Promise<T> {
  await api<T>(method, path, { body, dryRun: true });
  return api<T>(method, path, { body });
}

async function authorised(method: string, path: string, init: { query?: Query; body?: BodyInit; type?: string } = {}): Promise<Response> {
  const url = new URL(`${cfg().apiUrl.replace(/\/$/, "")}${path}`);
  for (const [k, v] of Object.entries(init.query ?? {})) if (v !== undefined) url.searchParams.set(k, String(v));
  const token = await accessToken();
  const res = await fetch(url, {
    method,
    headers: { ...(token ? { authorization: `Bearer ${token}` } : {}), ...(init.type ? { "content-type": init.type } : {}) },
    ...(init.body !== undefined ? { body: init.body } : {}),
  });
  if (!res.ok) {
    const p = ((await res.json().catch(() => ({}))) ?? {}) as { code?: string; detail?: string; title?: string; errors?: ProblemField[] };
    if (res.status === 401) signOut();
    throw new ApiError(res.status, p.code ?? "error", p.detail ?? p.title ?? `HTTP ${res.status}`, p.errors ?? []);
  }
  return res;
}

/**
 * Send a file as the raw body (design 3.11). No dry run first: that would send the file twice, and an
 * upload is one transaction, so a refused one changes nothing.
 */
export async function uploadFile<T>(path: string, file: File): Promise<T> {
  const res = await authorised("POST", path, { query: { name: file.name }, body: file, type: file.type || "application/octet-stream" });
  return (await res.json()) as T;
}

/** Fetch a stored file with the signed-in token and hand it to the browser as a download. */
export async function downloadFile(path: string, name: string): Promise<void> {
  const blob = await (await authorised("GET", path)).blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 60_000);
}
